import React, { useCallback, useEffect } from "react";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import { any, object, TypeOf, z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm, SubmitHandler, Controller } from "react-hook-form";
import {
  loadFacitlies,
  loadFacility,
  sendAttachment,
} from "../../redux/actions/people.actions";
import { useSelector } from "react-redux";
import CircularProgress from "@mui/material/CircularProgress";
import SaveIcon from "@mui/icons-material/Save";
import { green } from "@mui/material/colors";
import {
  Box,
  Button,
  TextField,
  Autocomplete,
  createFilterOptions,
  Checkbox,
  Chip,
  Stack,
} from "@mui/material";
import AttachmentIcon from "@mui/icons-material/Attachment";

import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import parse from "autosuggest-highlight/parse";
import match from "autosuggest-highlight/match";
import { confirm } from "react-confirm-box";
import Dilogbox from "../../components/Dilogbox";
import { useAppDispatch } from "../../redux/store";

dayjs.extend(customParseFormat);

interface Props {
  isDialogOpened: boolean;
  handleCloseDialog: any;
  action: string;
  attachments: any;
  customfields: any;
  user: any;
  handleAttachments: any;
}

const formSchema = object({
  id: any(),
  facility: z.any(),
  email: z
    .string()
    .min(1, "Email is required.")
    .regex(/^(\s?[^\s,]+@[^\s,]+\.[^\s,]+\s?,)*(\s?[^\s,]+@[^\s,]+\.[^\s,]+)$/),
  facility_contact_person: z.any(),
});

type FormInput = TypeOf<typeof formSchema>;

// describes the input data
type Category = {
  id: number;
  parent: any;
  facility_name: string;
  childCategories?: Category[];
};
interface Option {
  id: number;
  facility_name: string;
  depth: number;
  parentId: number | null;
  matchTerms: string[];
}

const toOptions = (
  category: Category,
  depth: number = 0,
  parentId: number | null = null
): Option[] => {
  const { id, facility_name, childCategories = [] } = category;
  const children = childCategories.flatMap((child) =>
    toOptions(child, depth + 1, id)
  );
  const option = {
    id,
    facility_name,
    depth,
    parentId,
    matchTerms: [facility_name].concat(
      children.map((obj) => obj.facility_name)
    ),
  };
  return [option].concat(children);
};

const customOptions = {
  render: (message: any, onConfirm: any, onCancel: any) => {
    return (
      <Dilogbox
        isDialogOpened={true}
        handleConfirmDialog={onConfirm}
        handleCloseDialog={onCancel}
        message={message}
      />
    );
  },
};

export function MailForm(props: Props) {
  const { handleCloseDialog } = props;
  //const dispatch = useDispatch()
  const dispatch = useAppDispatch();
  const { facilities, facility, sendMail, sendMailLoading } = useSelector(
    (state: any) => state.people
  );
  const {
    register,
    setValue,
    control,
    formState: { errors },
    handleSubmit,
  } = useForm<FormInput>({
    resolver: zodResolver(formSchema),
    defaultValues: { facility: null },
  });
  const [optionsList, setOptionsList] = React.useState<Option[]>([]);

  const handleClose = useCallback(() => {
    handleCloseDialog(false);
  }, [handleCloseDialog]);

  const [chipData, setChipData] = React.useState([...props.attachments]);

  useEffect(() => {
    if (props.isDialogOpened) dispatch<any>(loadFacitlies());
  }, [props.isDialogOpened, dispatch]);

  useEffect(() => {
    setChipData([...props.attachments]);
  }, [props.attachments]);

  useEffect(() => {
    if (sendMail) {
      handleClose();
    }
  }, [sendMail, handleClose]);

  useEffect(() => {
    if (facility) {
      setValue("email", facility.facility_contact_email);
      setValue("facility_contact_person", facility.facility_contact_person);
    }
  }, [setValue, facility]);

  React.useEffect(() => {
    if (facilities)
      setOptionsList(
        facilities.flatMap((category: any) => {
          return toOptions(category);
        })
      );
  }, [facilities]);

  const onFormSubmit: SubmitHandler<FormInput> = (data) => {
    const formData = new FormData();
    formData.append("facility", data.facility.id);
    formData.append("attachments", JSON.stringify(chipData));
    //formData.append('attachments', JSON.stringify({...chipData}))
    formData.append("user", props.user.id);
    formData.append("email", data.email);
    formData.append("facility_contact_person", data.facility_contact_person);

    //formData.append('', data.id)
    /* for (const value of formData.values()) {
            console.log(value);
          }
        */

    dispatch(sendAttachment(formData));
  };

  const handleDelete = (chipToDelete: number, options: any) => async () => {
    if (chipData.length === 1) {
      alert("Your mail should have at least one attachment.");
    } else {
      const result = await confirm("Are you sure you want to delete?", options);
      if (result) {
        setChipData((chips) => chips.filter((chip) => chip !== chipToDelete));
        let data = chipData.filter((chip: number) => chip !== chipToDelete);
        props.handleAttachments(data);
      }
    }
  };

  return (
    <Dialog open={props.isDialogOpened} onClose={handleClose} fullWidth>
      <DialogTitle>{props.action}</DialogTitle>
      <DialogContent>
        <Box
          component="form"
          id="companyform"
          name="companyform"
          noValidate
          onSubmit={handleSubmit(onFormSubmit)}
        >
          {props.isDialogOpened && optionsList && (
            <Controller
              control={control}
              name="facility"
              rules={{ required: true }}
              defaultValue=""
              render={({ field: { onChange, value } }) => (
                <Autocomplete
                  onChange={(event, item) => {
                    onChange(item);
                    dispatch<any>(loadFacility(item.id));
                  }}
                  value={value}
                  options={optionsList}
                  //getOptionLabel={(item) => item.facility_name}
                  isOptionEqualToValue={(option, value) =>
                    option.id === value.id
                  }
                  getOptionLabel={(item) =>
                    item.facility_name ? item.facility_name : ""
                  }
                  /*  getOptionSelected={(option:any, value:any) =>
                                         value === undefined || value === "" || option.id === value.id
                                       } */

                  renderOption={(props, option, { selected, inputValue }) => {
                    const matches = match(option.facility_name, inputValue);
                    const parts = parse(option.facility_name, matches);
                    return (
                      <li {...props}>
                        <Checkbox
                          checked={selected}
                          sx={{ ml: 2 * option.depth }}
                        />
                        <div>
                          {parts.map((part: any, index: any) => (
                            <span
                              key={index}
                              style={{
                                fontWeight: part.highlight ? 700 : 400,
                              }}
                            >
                              {part.text}
                            </span>
                          ))}
                        </div>
                      </li>
                    );
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="standard"
                      label="Facility Name"
                    />
                  )}
                  filterOptions={createFilterOptions({
                    // join with some arbitrary separator to prevent matches across adjacent terms
                    stringify: (option: any) => option.matchTerms.join("//"),
                  })}
                />
              )}
            />
          )}

          <TextField
            sx={{ mt: 1 }}
            id="email"
            label="Email"
            required
            fullWidth
            variant="standard"
            InputLabelProps={{
              shrink: true,
            }}
            error={!!errors["email"]}
            {...register("email")}
            helperText={errors["email"] ? errors["email"].message : ""}
          />

          <TextField
            sx={{ mt: 1 }}
            id="facility_contact_person"
            label="Contact Person"
            required
            fullWidth
            variant="standard"
            InputLabelProps={{
              shrink: true,
            }}
            error={!!errors["facility_contact_person"]}
            {...register("facility_contact_person")}
            helperText={
              errors["facility_contact_person"]
                ? errors["facility_contact_person"].message
                : ""
            }
          />

          <Stack direction="row" spacing={1} sx={{ mt: "15px" }}>
            <AttachmentIcon />
            {props.customfields.map((item: any) => {
              return (
                chipData.includes(item.id as string) && (
                  <Chip
                    label={item.field_label}
                    onDelete={handleDelete(item.id, customOptions)}
                  />
                )
              );
            })}
          </Stack>

          <DialogActions>
            <Box sx={{ m: 1, position: "relative" }}>
              <Button
                type="submit"
                form="companyform"
                variant="contained"
                color="success"
                startIcon={<SaveIcon />}
                disabled={sendMailLoading}
              >
                Send
              </Button>
              {sendMailLoading && (
                <CircularProgress
                  size={24}
                  sx={{
                    color: green[500],
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    marginTop: "-12px",
                    marginLeft: "-12px",
                  }}
                />
              )}
            </Box>
            <Button
              onClick={handleClose}
              variant="contained"
              color="error"
              disabled={sendMailLoading}
            >
              Cancel
            </Button>
          </DialogActions>
        </Box>
      </DialogContent>
    </Dialog>
  );
}
